import React from "react";

// Reviewer work of one review screen session that is not on the server yet (draft question
// comments, the general comment) plus the questions whose answers were saved in this session (for
// the "Edited" marker). Pure reducer and helpers are exported for node tests; this module only
// imports React so it loads outside the app runtime.

export interface DraftComment {
  questionId: number;
  text: string;
}

export interface ReviewSessionState {
  drafts: DraftComment[];
  editedQuestionIds: Set<number>;
  generalComment: string;
}

export type ReviewSessionAction =
  | { type: "setDraft"; questionId: number; text: string }
  | { type: "removeDraft"; questionId: number }
  | { type: "setGeneralComment"; text: string }
  | { type: "markEdited"; questionId: number }
  | { type: "reset" };

export const initialReviewSessionState: ReviewSessionState = {
  drafts: [],
  editedQuestionIds: new Set(),
  generalComment: "",
};

export function reviewSessionReducer(
  state: ReviewSessionState,
  action: ReviewSessionAction
): ReviewSessionState {
  switch (action.type) {
    case "setDraft": {
      // A blank draft is no draft: saving an emptied comment removes it.
      if (action.text.trim() === "") {
        return reviewSessionReducer(state, { type: "removeDraft", questionId: action.questionId });
      }
      const exists = state.drafts.some((draft) => draft.questionId === action.questionId);
      const drafts = exists
        ? state.drafts.map((draft) =>
            draft.questionId === action.questionId ? { ...draft, text: action.text } : draft
          )
        : [...state.drafts, { questionId: action.questionId, text: action.text }];
      return { ...state, drafts };
    }
    case "removeDraft": {
      if (!state.drafts.some((draft) => draft.questionId === action.questionId)) {
        return state;
      }
      return {
        ...state,
        drafts: state.drafts.filter((draft) => draft.questionId !== action.questionId),
      };
    }
    case "setGeneralComment":
      return { ...state, generalComment: action.text };
    case "markEdited": {
      if (state.editedQuestionIds.has(action.questionId)) {
        return state;
      }
      return {
        ...state,
        editedQuestionIds: new Set([...state.editedQuestionIds, action.questionId]),
      };
    }
    case "reset":
      // Only unsent comments are cleared: saved edits are already on the server and keep their
      // "Edited" marker.
      return { ...state, drafts: [], generalComment: "" };
  }
}

export function hasUnsentWork(state: Pick<ReviewSessionState, "drafts" | "generalComment">) {
  return state.drafts.length > 0 || state.generalComment.trim() !== "";
}

/** "Reopen for RN (2 comments)", or "Reopen for RN" when only a general comment is written. */
export function reopenButtonLabel(draftCount: number): string {
  if (draftCount === 0) {
    return "Reopen for RN";
  }
  return `Reopen for RN (${draftCount} ${draftCount === 1 ? "comment" : "comments"})`;
}

/** The reopen request body: trimmed texts, null general comment when blank. */
export function buildReopenRequest(state: Pick<ReviewSessionState, "drafts" | "generalComment">) {
  const generalComment = state.generalComment.trim();
  return {
    generalComment: generalComment === "" ? null : generalComment,
    questionComments: state.drafts.map((draft) => ({
      questionId: draft.questionId,
      comment: draft.text.trim(),
    })),
  };
}

/**
 * A submitted document edited in this session is not updated (submitted date, PDF) until it is
 * submitted again: the review then offers Submit and asks before closing.
 */
export function needsResubmit(params: {
  editedCount: number;
  hasScheduledDoc: boolean;
  isSubmitted: boolean;
}): boolean {
  return params.editedCount > 0 && params.isSubmitted && params.hasScheduledDoc;
}

export function useReviewSession() {
  const [state, dispatch] = React.useReducer(reviewSessionReducer, initialReviewSessionState);

  const actions = React.useMemo(
    () => ({
      setDraft: (questionId: number, text: string) =>
        dispatch({ type: "setDraft", questionId, text }),
      removeDraft: (questionId: number) => dispatch({ type: "removeDraft", questionId }),
      setGeneralComment: (text: string) => dispatch({ type: "setGeneralComment", text }),
      markEdited: (questionId: number) => dispatch({ type: "markEdited", questionId }),
      reset: () => dispatch({ type: "reset" }),
    }),
    []
  );

  return {
    drafts: state.drafts,
    generalComment: state.generalComment,
    editedQuestionIds: state.editedQuestionIds,
    hasUnsentWork: hasUnsentWork(state),
    ...actions,
  };
}

export type ReviewSession = ReturnType<typeof useReviewSession>;
